import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetView,
  type BottomSheetModalProps
} from '@gorhom/bottom-sheet'
import { useFocusEffect } from '@react-navigation/native'
import { IconHeart } from '@tabler/icons-react-native'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { useWindowDimensions, View } from 'react-native'
import { graphql, useFragment } from 'react-relay'

import { type ProfileLikeBottomSheet_profile$key } from '@/__generated__/ProfileLikeBottomSheet_profile.graphql'
import { HStack, Icon, Pressable, Text, VStack } from '@/components'
import { useAppState, useBottomSheetContainerStyle, useFocusableRef, useTimeout } from '@/hooks'
import {
  redBottomSheetModalBackgroundStyle,
  redBottomSheetModalHandleIndicatorStyle,
  redBottomSheetModalHandleStyle,
  shadowStyles
} from '@/lib'

export interface ProfileLikeBottomSheetProps extends Omit<BottomSheetModalProps, 'children'> {
  onDismiss?: () => void
  profileRef: ProfileLikeBottomSheet_profile$key | null | undefined
}

export type ProfileLikeBottomSheetRef = BottomSheetModal

export const ProfileLikeBottomSheet = forwardRef<BottomSheetModal, ProfileLikeBottomSheetProps>(
  ({ containerStyle, onChange, onDismiss, profileRef, ...props }, ref) => {
    const [headerRef, focusHeader] = useFocusableRef<View>(true)
    const dimensions = useWindowDimensions()
    const { setHideMatchSheet } = useAppState()
    const bottomSheetModalRef = useRef<BottomSheetModal>(null)
    const profileData = useFragment(
      graphql`
        fragment ProfileLikeBottomSheet_profile on Profile {
          name
        }
      `,
      profileRef
    )
    const timeout = useTimeout(() => bottomSheetModalRef.current?.dismiss(), 3000)
    const onHideMatchSheetPress = useCallback(async () => {
      await setHideMatchSheet(true)
      bottomSheetModalRef.current?.dismiss()
    }, [setHideMatchSheet])

    useImperativeHandle(ref, () => bottomSheetModalRef.current!)

    return (
      <BottomSheetModal
        accessible={false}
        accessibilityRole='alert'
        accessibilityLiveRegion='assertive'
        backdropComponent={(props) => (
          <BottomSheetBackdrop
            {...props}
            appearsOnIndex={0}
            disappearsOnIndex={-1}
            pressBehavior='close'
          />
        )}
        backgroundStyle={redBottomSheetModalBackgroundStyle}
        containerStyle={useBottomSheetContainerStyle(containerStyle)}
        enableDismissOnClose
        enableDynamicSizing
        enablePanDownToClose
        handleIndicatorStyle={redBottomSheetModalHandleIndicatorStyle}
        handleStyle={redBottomSheetModalHandleStyle}
        maxDynamicContentSize={useMemo(() => dimensions.height * 0.5, [dimensions.height])}
        onChange={(position: number) => {
          if (position === 0) {
            focusHeader()
            timeout.start()
          }

          if (position === -1) {
            timeout.clear()
          }
        }}
        onDismiss={useCallback(() => {
          timeout.clear()
          onDismiss?.()
        }, [onDismiss, timeout])}
        ref={bottomSheetModalRef}
        role='alertdialog'
        {...props}
      >
        <BottomSheetView focusHook={useFocusEffect}>
          <VStack className='pb-safe-or-4 items-center justify-center gap-4 p-4'>
            <HStack
              accessible={false}
              accessibilityElementsHidden
              aria-hidden
              importantForAccessibility='no-hide-descendants'
              className='aspect-square w-[15%] items-center justify-center rounded-full bg-brand-orange-500'
              style={shadowStyles.whiteGlow}
            >
              <Icon
                as={IconHeart}
                className='h-1/2 w-1/2 fill-white'
              />
            </HStack>
            <HStack
              accessible
              accessibilityLabel={`You liked ${profileData?.name}!`}
              accessibilityRole='header'
              className='gap-4'
              focusable
              nativeID='ProfileLikeBottomSheetHeader'
              importantForAccessibility='yes'
              ref={headerRef}
              tabIndex={0}
            >
              <Text
                accessible={false}
                accessibilityElementsHidden
                aria-hidden
                importantForAccessibility='no-hide-descendants'
                className='text-center font-poppinsSemiBold text-lg text-white'
              >
                You liked {profileData?.name}!
              </Text>
            </HStack>
            <Text className='text-center text-white'>If they like you back, they will appear in the Messages tab.</Text>
            <Pressable
              accessibilityRole='button'
              onPress={onHideMatchSheetPress}
            >
              <Text className='font-poppinsMedium text-xs text-white underline'>Don't show this again</Text>
            </Pressable>
          </VStack>
        </BottomSheetView>
      </BottomSheetModal>
    )
  }
)

ProfileLikeBottomSheet.displayName = 'ProfileLikeBottomSheet'
